/**
 * "Where we met" (2026-10-06): a place, an occasion and a date that either of
 * you can record. It opens the shared history — the first thing above your
 * letters — because it is where the story starts.
 *
 * Shared, so it is fixed in what it can say: your own words for the place and
 * the occasion, a date, and which of you wrote it. Nothing is inferred.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, MapPinned, PenLine, Plus } from 'lucide-react';
import { MET_EVENT_MAX_CHARS, MET_PLACE_MAX_CHARS, metPhrase } from '@toiyp/shared';
import { apiErrorMessage } from '../letters/ui';
import { useMetQuery, useSaveMetMutation, type MetView } from '../relationships/api';

/** `2019-03-12` → "12 March 2019", read as a calendar day (no zone shift). */
export function metDateLabel(metOn: string | null): string | null {
  if (!metOn) return null;
  const d = new Date(`${metOn}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function MetForm({
  relationshipId,
  current,
  onDone,
}: {
  relationshipId: string;
  current: MetView | null;
  onDone: () => void;
}) {
  const [saveMet, { isLoading }] = useSaveMetMutation();
  const [place, setPlace] = useState(current?.place ?? '');
  const [event, setEvent] = useState(current?.event ?? '');
  const [metOn, setMetOn] = useState(current?.metOn ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPlace(current?.place ?? '');
    setEvent(current?.event ?? '');
    setMetOn(current?.metOn ?? '');
  }, [current]);

  const save = async (clear = false) => {
    setError(null);
    try {
      await saveMet({
        relationshipId,
        place: clear ? null : place.trim() || null,
        event: clear ? null : event.trim() || null,
        metOn: clear ? null : metOn || null,
      }).unwrap();
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not save where you met'));
    }
  };

  return (
    <form
      className="met-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="field">
        <span className="field__label">The occasion</span>
        <input
          className="field__input"
          value={event}
          maxLength={MET_EVENT_MAX_CHARS}
          onChange={(e) => setEvent(e.target.value)}
          placeholder="Rita's wedding, a night class, the 8:14 train…"
        />
      </label>
      <label className="field">
        <span className="field__label">The place</span>
        <input
          className="field__input"
          value={place}
          maxLength={MET_PLACE_MAX_CHARS}
          onChange={(e) => setPlace(e.target.value)}
          placeholder="Lisbon"
        />
      </label>
      <label className="field">
        <span className="field__label">When</span>
        <input
          className="field__input"
          type="date"
          value={metOn}
          max={todayIso()}
          onChange={(e) => setMetOn(e.target.value)}
        />
      </label>
      <p className="field__hint">You both see this, and either of you can change it.</p>
      {error && (
        <p className="form-error" role="alert">
          <AlertCircle size={13} aria-hidden="true" /> {error}
        </p>
      )}
      <div className="met-form__actions">
        {current && (
          <button type="button" className="btn btn--ghost btn--sm met-form__clear" disabled={isLoading} onClick={() => void save(true)}>
            Remove
          </button>
        )}
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary btn--sm" disabled={isLoading}>
          {isLoading ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/**
 * The tag itself. `thread` is the first item of the letter history; `card` is
 * the same memory on the contact's summary page.
 */
export function MetTag({
  relationshipId,
  peerName,
  variant = 'thread',
}: {
  relationshipId: string;
  peerName: string;
  variant?: 'thread' | 'card';
}) {
  const { data: met, isError, isLoading } = useMetQuery({ relationshipId });
  const [editing, setEditing] = useState(false);

  // A locked vault answers 404 for a hidden pair — show nothing, never the old copy.
  if (isError || isLoading || met === undefined) return null;

  const cls = `met-tag met-tag--${variant}`;

  if (editing) {
    return (
      <div className={cls}>
        <span className="met-tag__label">
          <MapPinned size={13} aria-hidden="true" /> Where you met
        </span>
        <MetForm relationshipId={relationshipId} current={met} onDone={() => setEditing(false)} />
      </div>
    );
  }

  if (!met) {
    return (
      <button type="button" className={`${cls} met-tag--empty`} onClick={() => setEditing(true)}>
        <Plus size={13} aria-hidden="true" /> Where did you and @{peerName} meet?
      </button>
    );
  }

  const phrase = metPhrase(met);
  const when = metDateLabel(met.metOn);
  return (
    <div className={cls}>
      <span className="met-tag__label">
        <MapPinned size={13} aria-hidden="true" /> Where you met
      </span>
      <p className="met-tag__what">
        {phrase ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : `You met @${peerName}`}
      </p>
      {when && <p className="met-tag__when">{when}</p>}
      <span className="met-tag__foot">
        {!met.byMe && met.updatedByHandle && <span>noted by @{met.updatedByHandle}</span>}
        <button type="button" className="met-tag__edit" onClick={() => setEditing(true)} aria-label="Edit where you met">
          <PenLine size={12} aria-hidden="true" /> edit
        </button>
      </span>
    </div>
  );
}
